import { useContext, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  Collapse,
  Container,
  Group,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { IconArrowLeft, IconAlertTriangle } from "@tabler/icons-react";
import { addDoc, collection, doc, getDoc, Timestamp } from "firebase/firestore";
import { GenevaApiSelect } from "../../components/GenevaApiSelect";
import { MyUserDataContext } from "../../contexts";
import { useFirestore } from "../../helpers/firebaseContext";
import { toMantineDate } from "../../helpers/mantineDates";
import { CustomClaims } from "../../types";
import {
  EXPENSE_FREQUENCIES,
  ExpenseFrequency,
  ExpenseManagerDoc,
  ExpenseManagerRow,
  formatAmount,
  occurrences,
  splitAmount,
} from "../../helpers/expenseManager";

const OCCURRENCE_WARNING_THRESHOLD = 750;

export default function ExpenseManagerCreate() {
  const navigate = useNavigate();
  const db = useFirestore();
  const userContext = useContext(MyUserDataContext);

  const customClaims = (userContext.firebaseTokenResult?.claims?.customClaims as CustomClaims) || {};
  const canCreate = Boolean(customClaims.geneva_requests_create || customClaims.geneva_requests_approve);

  // Copy feature: an existing manager passed via navigation state seeds the
  // form (original endDate, not effectiveEndDate — closures are not copied).
  const copyFrom = (useLocation().state as { copyFrom?: ExpenseManagerRow } | null)?.copyFrom;

  const [portfolio, setPortfolio] = useState<string | null>(copyFrom?.portfolio ?? null);
  const [locationAccount, setLocationAccount] = useState<string | null>(copyFrom?.locationAccount ?? null);
  const [fromAccount, setFromAccount] = useState<string | null>(copyFrom?.fromAccount ?? null);
  const [toAccount, setToAccount] = useState<string | null>(copyFrom?.toAccount ?? null);
  const [eventDesc, setEventDesc] = useState<string | null>(copyFrom?.eventDesc ?? null);
  const [comments, setComments] = useState(copyFrom?.comments ?? "");
  const [frequency, setFrequency] = useState<ExpenseFrequency>(copyFrom?.frequency ?? "monthly");
  const [startDate, setStartDate] = useState<string | null>(copyFrom?.startDate ?? null);
  const [endDate, setEndDate] = useState<string | null>(copyFrom?.endDate ?? null);
  const [amountMode, setAmountMode] = useState<"total" | "perPeriod">(copyFrom?.amountMode ?? "total");
  const [amount, setAmount] = useState<number | string>(
    copyFrom ? (copyFrom.amountMode === "total" ? copyFrom.totalAmount : copyFrom.perPeriodAmount) : "",
  );
  const [showOccurrences, setShowOccurrences] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const dates = useMemo(
    () => (startDate && endDate && startDate <= endDate ? occurrences(startDate, endDate, frequency) : []),
    [startDate, endDate, frequency],
  );

  const amountNumber = typeof amount === "number" ? amount : parseFloat(amount || "");
  const hasAmount = Number.isFinite(amountNumber) && amountNumber > 0;

  const amounts = useMemo(() => {
    if (!dates.length || !hasAmount) return [];
    return amountMode === "total"
      ? splitAmount(amountNumber, dates.length)
      : dates.map(() => amountNumber);
  }, [dates, amountMode, amountNumber, hasAmount]);

  const totalAmount = useMemo(
    () => amounts.reduce((s, v) => s + Math.round(v * 1e6), 0) / 1e6,
    [amounts],
  );

  const validationError = !portfolio || !locationAccount || !fromAccount || !toAccount
    ? "Select portfolio, location account and both financial accounts."
    : !startDate || !endDate
      ? "Select start and end dates."
      : startDate > endDate
        ? "Start date must be on or before the end date."
        : !hasAmount
          ? "Enter an amount greater than 0."
          : fromAccount === toAccount
            ? "From and To accounts must be different."
            : !comments.trim()
              ? "Enter comments."
              : null;

  const handleSubmit = async () => {
    if (validationError || !startDate || !endDate) return;
    setSubmitting(true);
    const user = userContext.user;

    // Fund portfolios (their id exists in the funds collection) must carry
    // FundStructure = portfolio id on every journal entry.
    let isFund = false;
    try {
      isFund = (await getDoc(doc(db, "funds", portfolio!))).exists();
    } catch (error) {
      console.error("Could not check funds collection", error);
    }

    const expenseDoc: ExpenseManagerDoc = {
      status: "pending",
      portfolio: portfolio!,
      ...(isFund ? { fundStructure: portfolio! } : {}),
      locationAccount: locationAccount!,
      fromAccount: fromAccount!,
      toAccount: toAccount!,
      comments: comments.trim(),
      ...(eventDesc ? { eventDesc } : {}),
      frequency,
      startDate,
      endDate,
      amountMode,
      totalAmount,
      perPeriodAmount: amounts[0] ?? 0,
      occurrenceCount: dates.length,
      audit: {
        createdAt: Date.now(),
        createdBy: user?.uid || "",
        createdByEmail: user?.email || "",
        createdByName: user?.displayName || "",
      },
    };

    try {
      const docRef = await addDoc(collection(db, "expense_managers"), expenseDoc);
      await addDoc(collection(db, "expense_managers", docRef.id, "history"), {
        msg: `Created (${dates.length} entries) — pending approval`,
        details: `${frequency} ${formatAmount(amounts[0] ?? 0)} from ${fromAccount} to ${toAccount}, ${startDate} → ${endDate}, total ${formatAmount(totalAmount)}`,
        by: user?.email ?? null,
        byID: user?.uid ?? null,
        date: Timestamp.now(),
      });
      notifications.show({
        title: "Expense manager created",
        message: "Waiting for approval before sending to Geneva.",
        color: "blue",
      });
      navigate(`/geneva_requests/expense_managers/${docRef.id}`);
    } catch (error) {
      notifications.show({
        title: "Create failed",
        message: error instanceof Error ? error.message : String(error),
        color: "red",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!canCreate) {
    return (
      <Container size="xl" py="md">
        <Alert color="red" title="Not authorized">
          You do not have permission to manage expense managers.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="xl">
        <Box>
          <Button
            variant="subtle"
            size="compact-md"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/geneva_requests/expense_managers")}
          >
            Back to Expense Managers
          </Button>
          <Title order={2} mt="sm">{copyFrom ? "Copy Expense Manager" : "Create Expense Manager"}</Title>
          <Text c="dimmed" size="sm">
            Recurring PrePaid journal entries, sent to Geneva once approved.
          </Text>
        </Box>
      </Group>

      <Card shadow="sm" p="md" withBorder>
        <Stack gap="md">
          <Group grow align="flex-start">
            <GenevaApiSelect linkTable="Portfolio" label="Portfolio" required value={portfolio} onChange={setPortfolio} />
            <GenevaApiSelect linkTable="LocationAccount" label="Location Account" required value={locationAccount} onChange={setLocationAccount} />
          </Group>
          <Group grow align="flex-start">
            <GenevaApiSelect linkTable="FinancialAccount" label="From Account (Debit)" required value={fromAccount} onChange={setFromAccount} />
            <GenevaApiSelect linkTable="FinancialAccount" label="To Account (Credit)" required value={toAccount} onChange={setToAccount} />
          </Group>
          <Group grow align="flex-start">
            <GenevaApiSelect linkTable="EventsType" label="Event Desc" value={eventDesc} onChange={setEventDesc} />
            <Select
              label="Frequency"
              required
              data={EXPENSE_FREQUENCIES}
              value={frequency}
              onChange={(value) => value && setFrequency(value as ExpenseFrequency)}
              allowDeselect={false}
            />
          </Group>
          <Group grow align="flex-start">
            <DatePickerInput
              label="Start Date"
              required
              value={startDate}
              onChange={(value) => setStartDate(toMantineDate(value))}
            />
            <DatePickerInput
              label="End Date"
              required
              value={endDate}
              onChange={(value) => setEndDate(toMantineDate(value))}
            />
          </Group>
          <Group grow align="flex-end">
            <Box>
              <Text size="sm" fw={500} mb={4}>Amount Entry</Text>
              <SegmentedControl
                fullWidth
                data={[
                  { value: "total", label: "Total Amount" },
                  { value: "perPeriod", label: "Per-Period Amount" },
                ]}
                value={amountMode}
                onChange={(value) => setAmountMode(value as "total" | "perPeriod")}
              />
            </Box>
            <NumberInput
              label={amountMode === "total" ? "Total Amount" : "Per-Period Amount"}
              required
              min={0}
              decimalScale={6}
              hideControls
              value={amount}
              onChange={setAmount}
            />
          </Group>
          <Textarea
            label="Comments"
            required
            value={comments}
            onChange={(event) => setComments(event.currentTarget.value)}
          />

          {dates.length > 0 && hasAmount && (
            <Paper p="sm" withBorder bg="var(--mantine-color-default-hover)">
              <Group justify="space-between">
                <Text size="sm">
                  <b>{dates.length}</b> entries from <b>{dates[0]}</b> to <b>{dates[dates.length - 1]}</b>
                  {" — "}
                  {amountMode === "total"
                    ? <>per period <b>{formatAmount(amounts[0] ?? 0)}</b>{amounts.length > 1 && amounts[amounts.length - 1] !== amounts[0] && <> (last {formatAmount(amounts[amounts.length - 1])})</>}</>
                    : <>total <b>{formatAmount(totalAmount)}</b></>}
                </Text>
                <Button variant="subtle" size="compact-sm" onClick={() => setShowOccurrences((v) => !v)}>
                  {showOccurrences ? "Hide" : "Show"} entries
                </Button>
              </Group>
              <Collapse expanded={showOccurrences}>
                <Table striped highlightOnHover mt="sm">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>#</Table.Th>
                      <Table.Th>Date</Table.Th>
                      <Table.Th>Amount</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {dates.map((date, n) => (
                      <Table.Tr key={date}>
                        <Table.Td>{n + 1}</Table.Td>
                        <Table.Td>{date}</Table.Td>
                        <Table.Td>{formatAmount(amounts[n])}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Collapse>
            </Paper>
          )}

          {dates.length > OCCURRENCE_WARNING_THRESHOLD && (
            <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
              This will create {dates.length} journal entries in Geneva. Consider a longer period frequency.
            </Alert>
          )}

          <Group justify="flex-end">
            <Button
              onClick={handleSubmit}
              loading={submitting}
              disabled={Boolean(validationError)}
              title={validationError ?? undefined}
            >
              Submit for Approval
            </Button>
          </Group>
          {validationError && (
            <Text size="xs" c="dimmed" ta="right">{validationError}</Text>
          )}
        </Stack>
      </Card>
    </Container>
  );
}
